import { useSelector } from 'react-redux';
import { FETCH_ERRORS } from '../constants';
import {
  selectAggStatsFilters,
  selectAggStatsLoading,
  selectCampaignIdsToFetchMetrics,
  selectCampaignMetricsTableData,
  selectCampaignsFilters,
  selectCampaignsLoading,
  selectDeviceData,
  selectError,
  selectLocationData,
  selectOverallPerformanceData,
  selectTimeSeriesData,
} from '../selectors';
import { RestDataIdentifier, RestQueryParams } from '../types';

// Map data identifiers to their corresponding error keys
const fetchErrorKey: Record<RestDataIdentifier, string> = {
  [RestDataIdentifier.DEVICE]: FETCH_ERRORS.FETCH_DEVICE_DATA,
  [RestDataIdentifier.LOCATION]: FETCH_ERRORS.FETCH_LOCATION_DATA,
  [RestDataIdentifier.TIME_SERIES]: FETCH_ERRORS.FETCH_TIME_SERIES_DATA,
  [RestDataIdentifier.CAMPAIGNS_FOR_METRICS]: FETCH_ERRORS.FETCH_CAMPAIGNS,
  [RestDataIdentifier.OVERVIEW]: FETCH_ERRORS.FETCH_X_MED_AGG_STATS,
  [RestDataIdentifier.PDF_REPORT]: FETCH_ERRORS.FETCH_PDF_REPORT,
};

/**
 * Custom hook to access and process data from the Redux store based on the provided data identifier.
 *
 * @param {RestDataIdentifier} restDataIdentifier - The identifier for the type of data to fetch.
 * @returns {Object} An object containing the fetched data, previous query data, loading state, and error state.
 * @returns {Record<string, any>} data - The fetched data.
 * @returns {RestQueryParams} previousQueryFilters - The previous query filter state.
 * @returns {boolean} loading - The loading state.
 * @returns {boolean} error - The error state.
 */
export const useReduxData = (restDataIdentifier: RestDataIdentifier) => {
  // Rest data stored in the redux store
  const overallMetrics = useSelector(selectOverallPerformanceData);
  const deviceMetrics = useSelector(selectDeviceData);
  const locationMetrics = useSelector(selectLocationData);
  const timeSeriesMetrics = useSelector(selectTimeSeriesData);
  const aggregatesLoading = useSelector(selectAggStatsLoading);
  const aggregateFilters = useSelector(selectAggStatsFilters);
  const campaignMetricsTableData = useSelector(selectCampaignMetricsTableData);
  const campaignIdsToFetchMetrics = useSelector(selectCampaignIdsToFetchMetrics);
  const campaignsLoading = useSelector(selectCampaignsLoading);
  const campaignsFilters = useSelector(selectCampaignsFilters);

  // Error handling
  const errors = useSelector(selectError) || {};
  const showErrorState = Object.keys(errors).includes(fetchErrorKey[restDataIdentifier]);

  let data;
  let loading = false;
  let previousQueryFilters: RestQueryParams = {};

  switch (restDataIdentifier) {
    case RestDataIdentifier.LOCATION:
    case RestDataIdentifier.DEVICE: {
      const bucketMetrics = {
        [RestDataIdentifier.DEVICE]: deviceMetrics,
        [RestDataIdentifier.LOCATION]: locationMetrics,
      }[restDataIdentifier];
      const fetchValueKey = {
        [RestDataIdentifier.DEVICE]: 'Device Data',
        [RestDataIdentifier.LOCATION]: 'City Data',
      }[restDataIdentifier];

      data = bucketMetrics.data?.mediumMetrics?.[fetchValueKey] ?? {};
      loading = bucketMetrics.loading;
      previousQueryFilters = bucketMetrics.filter;
      break;
    }

    case RestDataIdentifier.TIME_SERIES:
      data = timeSeriesMetrics.data;
      loading = timeSeriesMetrics.loading;
      previousQueryFilters = timeSeriesMetrics.filter;
      break;

    case RestDataIdentifier.CAMPAIGNS_FOR_METRICS:
      /**
       * Combining these two data points because they originate
       * from the same REST query to the '/analytics/getCampaignsForMetrics' endpoint
       */
      data = {
        campaignMetricsTableData,
        campaignIdsToFetchMetrics,
      };
      loading = campaignsLoading;
      previousQueryFilters = campaignsFilters;
      break;

    case RestDataIdentifier.OVERVIEW:
      data = overallMetrics;
      loading = aggregatesLoading;
      previousQueryFilters = aggregateFilters;
      break;

    default:
      data = {};
      break;
  }

  return { data, previousQueryFilters, loading, error: showErrorState };
};
